/**
 * Central UI strings for QuickInbox.
 * Single source of truth for all user-facing text.
 * English only.
 */

const strings = {
  // ── Countdown & Expiry ──
  countdown: {
    expiresIn: (time) => `This address expires in ${time}`,
    expiresSoon: (time) => `Expires in ${time}`,
    expired: "Expired",
    keepForever: "Keep forever",
    expiredBanner: "This inbox has expired. All content has been deleted.",
    createNew: "Create new email",
  },

  // ── Folder Tabs ──
  folders: {
    inbox: "Inbox",
    sent: "Sent",
    compose: "Compose",
  },

  // ── Email List ──
  emailList: {
    noEmails: "No emails yet",
    noSentEmails: "No sent emails yet",
    noEmailsDesc: "Copy the address above and use it anywhere. Emails will appear here automatically.",
    noSentDesc: "Compose an email to see it here.",
    waiting: "Waiting for incoming emails...",
    selectToRead: "Select an email to read",
    selectSent: "Select a sent email to read",
    sendTest: "Send test",
    viewExamples: "View examples",
    newEmails: (count) => `${count} new email${count > 1 ? "s" : ""} received`,
    toPrefix: "To:",
  },

  // ── Email Detail ──
  emailDetail: {
    archive: "Archive",
    delete: "Delete",
    sent: "Sent",
    downloadNow: "Download attachments now — address and content will be deleted after expiry.",
    saveForever: "Keep forever",
  },

  // ── Attachments ──
  attachments: {
    count: (n) => `${n} attachment${n > 1 ? "s" : ""}`,
    rejectedSingle: "1 attachment could not be saved",
    rejectedMultiple: (n) => `${n} attachments could not be saved`,
    allowedFormats: "Allowed formats: JPG, PNG, PDF (max 5 MB per file)",
    downloadFailed: "Download failed",
    // Rejection reason labels (mapped from backend codes)
    reasons: {
      file_type_not_allowed: "File type not allowed",
      storage_limit_reached: "Storage limit reached",
      file_too_large: "File too large (max 5 MB)",
      download_failed: "Download failed",
    },
  },

  // ── Compose ──
  compose: {
    title: "Compose email",
    to: "To",
    subject: "Subject",
    message: "Message",
    send: "Send",
    sending: "Sending...",
    sendFailed: "Send failed",
    composeEmail: "Compose email",
  },

  // ── Auth / Access ──
  auth: {
    accessCode: "Access code",
    enterAccessCode: "Enter your access code to access this inbox.",
    unlock: "Unlock",
    unlocking: "Unlocking...",
    invalidAccessCode: "Invalid access code. Please try again.",
    tooManyAttempts: "Too many attempts. Please try again later.",
    orCreateNew: "or create a new email",
  },

  // ── Invite Friends ──
  share: {
    button: "Invite friends",
    title: "Invite friends",
    subtitle: "Recommend QuickInbox to others via email.",
    /* The personal `/r/{code}` link is injected in by SharePopover at
       render time so users always see (and copy) the correct attribution
       URL. Falls back to the bare landing page only if the code lookup
       hasn't returned yet — SharePopover hides the link box in that
       transient state. */
    defaultMessage: (link) =>
      "Hey — I’ve been using QuickInbox for temporary anonymous email. It’s free, instant, and no signup is needed.\n\n" +
      "Use my invite link:\n" +
      `${link}\n\n` +
      "Create your inbox in seconds.",
    toLabel: "To",
    toPlaceholder: "email@example.com; another@example.com",
    toHint: "Separate multiple addresses with semicolon (;)",
    messageLabel: "Message",
    send: "Send",
    sending: "Sending...",
    sent: "Sent",
    sendFailed: "Failed to send. Please try again.",
    invalidEmail: (email) => `Invalid email address: ${email}`,
    enterRecipient: "Please enter at least one email address.",
    enterMessage: "Please enter a message.",
    successSingle: "Recommendation sent!",
    successMultiple: (n) => `Recommendation sent to ${n} recipients`,
  },

  // ── Landing / Create ──
  landing: {
    addressTaken: "This address is already taken. Please choose another.",
    invalidAddress: "Please enter a valid address.",
  },

  // ── Inbox Page (Authenticated) ──
  inboxPage: {
    newEmail: "New email",
  },

  // ── Upgrade ──
  upgrade: {
    dontLose: "Don't lose this address",
    dontLoseDesc: "Keep your address, emails, and attachments permanently.",
    upgrade: "Upgrade",
    copyEmail: "Copy email",
    emailCopied: "Email copied",
    upgradeHint: "Upgrade to keep this address and access more features",
  },

  // ── Toast Messages ──
  toasts: {
    inboxReady: "Your temporary email is ready. You are now in your inbox.",
    demoGenerated: "Demo emails generated",
    testSent: "Test email sent",
    addressCopied: "Email address copied",
    linkCopied: "Link copied",
  },

  // ── CountdownBanner (legacy component) ──
  countdownBanner: {
    expired: "This address has expired",
    remaining: "remaining",
  },

  // ── Storage ──
  storage: {
    label: "Storage",
    full: "Storage full — new attachments will not be saved",
    almostFull: (mb) => `Storage almost full — ${mb} MB remaining`,
  },
};

export default strings;
